import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../App';
import { normalizePartnerLegalBundle, PARTNER_LEGAL_CONSENT_TEXT } from './Onboarding';

// AGB-122-STORNO-LIVE: Bestätigung einer neuen Fassung der Partner-Vertragsunterlagen, während
// das Portal nutzbar bleibt. Derselbe Annahmeweg (POST /partners/accept-agb mit exakter
// Dokumentliste) und derselbe Zustimmungssatz wie im Onboarding.
export default function PartnerLegalUpdate() {
  const { onboarding, refreshPartner } = useAuth();
  const navigate = useNavigate();
  const mounted = useRef(false);
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setChecked(false);
    try {
      const response = await api.get('/legal/bundles/partner', { timeout: 10000 });
      if (mounted.current) setBundle(normalizePartnerLegalBundle(response.data));
    } catch {
      if (!mounted.current) return;
      setBundle(null);
      setLoadError('Die Partner-Vertragsunterlagen sind derzeit nicht vollständig verfügbar. Eine Annahme ist deshalb nicht möglich.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = onboarding?.legalAcceptancePending === true;
  const accept = async () => {
    if (!checked || !bundle) return;
    setBusy(true);
    setAcceptError('');
    const documents = bundle.documents.map(({ type, version, content_hash }) => ({ type, version, content_hash }));
    try {
      await api.post('/partners/accept-agb', { accepted: true, documents }, { timeout: 15000 });
      await refreshPartner();
      if (!mounted.current) return;
      toast.success('Vertragsunterlagen bestätigt');
      navigate('/');
    } catch (error) {
      if (!mounted.current) return;
      const code = error.response?.data?.error;
      setChecked(false);
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        setAcceptError('Keine Antwort bestätigt. Bitte lade die Seite neu und prüfe den Stand, bevor du erneut bestätigst.');
      } else if (code === 'stale_legal_bundle' || code === 'legal_bundle_ambiguous') {
        setAcceptError('Die Vertragsunterlagen wurden zwischenzeitlich geändert. Bitte prüfe die neu geladene Version und stimme erneut zu.');
        await load();
      } else {
        setAcceptError('Die Annahme konnte nicht beweissicher gespeichert werden. Bitte versuche es erneut.');
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <section data-testid="partner-legal-update" style={{ maxWidth: 820 }}>
      <h2 style={{ fontSize: 16, margin: '0 0 6px' }}>Partner-Vertragsunterlagen</h2>
      {pending ? (
        <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.55, margin: '0 0 14px' }}>
          Es liegt eine neue Fassung vor. Dein Portal bleibt nutzbar. Bitte prüfe die Unterlagen und bestätige sie.
        </p>
      ) : (
        <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.55, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle size={16} color="#9C27B0" /> Du hast die aktuelle Fassung bereits bestätigt.
        </p>
      )}
      {loading && <div aria-live="polite" style={{ padding: 14, marginBottom: 14, background: '#f9fafb', borderRadius: 8, color: '#4b5563', fontSize: 13 }}>Verbindliche Vertragsunterlagen werden geladen …</div>}
      {loadError && (
        <div role="alert" style={{ padding: 14, marginBottom: 14, border: '1px solid #fca5a5', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: 13, lineHeight: 1.5 }}>
          {loadError}
          <button type="button" onClick={load} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, background: 'transparent', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 10px', color: '#991b1b', cursor: 'pointer' }}><RefreshCw size={14} /> Erneut laden</button>
        </div>
      )}
      {bundle && (
        <div data-testid="partner-legal-update-bundle" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          {bundle.release_version && <div style={{ fontSize: 12, color: '#4b5563' }}>Vertragspaket {bundle.release_version}</div>}
          {bundle.documents.map((document) => (
            <article key={document.type} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <strong style={{ color: '#374151', fontSize: 13 }}>{document.title}</strong>
                <span style={{ color: '#4b5563', fontSize: 12 }}>Version {document.version}</span>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <a href={document.url} target="_blank" rel="noreferrer" style={{ color: '#111', fontSize: 12, fontWeight: 700, textDecoration: 'underline' }}>Speicherbare Fassung öffnen</a>
                <span title={document.content_hash} style={{ color: '#6b7280', fontSize: 12 }}>Prüfsumme {document.content_hash.slice(0, 12)}…</span>
              </div>
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#374151' }}>Inhalt hier anzeigen</summary>
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb', whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.65, color: '#4b5563', maxHeight: 280, overflowY: 'auto' }}>{document.content}</div>
              </details>
            </article>
          ))}
        </div>
      )}
      {acceptError && <div role="alert" style={{ marginBottom: 14, color: '#991b1b', fontSize: 13, lineHeight: 1.5 }}>{acceptError}</div>}
      {pending && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={checked} disabled={!bundle || busy} onChange={(event) => setChecked(event.target.checked)} style={{ width: 18, height: 18 }} />
            <span>{PARTNER_LEGAL_CONSENT_TEXT}</span>
          </label>
          <button type="button" onClick={accept} disabled={!checked || busy || !bundle}
            style={{ background: !checked || busy || !bundle ? '#d1d5db' : '#111', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, cursor: !checked || busy || !bundle ? 'not-allowed' : 'pointer' }}>
            Neue Fassung bestätigen
          </button>
        </>
      )}
    </section>
  );
}
